import { useState } from 'react';

import { BuildingIcon, LockIcon, MailIcon, TagIcon, UserIcon } from './icons/index.js';

/** PremiumField's leading-icon names — subset of @kilat/ui's full icon set
 *  (the rest of the icons aren't field-leading). */
export type PremiumFieldIconName = 'building' | 'user' | 'mail' | 'tag';

/**
 * Premium form field primitives. ONE TRUE source. Extracted in Sprint 77
 * right before `apps/marketing/src/routes/become-an-agent-signup.tsx`
 * became the second caller — per [[feedback-unified-packages-ui]] rule
 * "extract before the second copy lands".
 *
 * `<PremiumField>` is the workhorse: filled-style input with optional
 * leading icon, focus-ring with brand-tinted halo, label + hint
 * subscriptions. `<PhoneField>` overlays a country-flag prefix block.
 * `<PasswordField>` adds show/hide + a live strength meter.
 *
 * Brand-themed via CSS variables with fallbacks so the components still
 * render correctly on surfaces that haven't loaded a tenant CSS layer
 * (e.g., NoTenantScreen). Per [[feedback-premium-custom-ui]] this is
 * standard HTML primitives styled fully custom — no stock framework
 * look — and respects the practical "use HTML inputs, not reinvented
 * DOM" interpretation.
 */

// ── PremiumField ──────────────────────────────────────────────────────

export type FieldHintTone = 'muted' | 'success' | 'warn';

export interface PremiumFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly type?: string;
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
  readonly autoComplete?: string;
  readonly autoCapitalize?: string;
  readonly minLength?: number;
  readonly hint?: string;
  readonly hintTone?: FieldHintTone;
  readonly icon?: PremiumFieldIconName;
  /**
   * A short status beside the label, like "12/80".
   *
   * Deliberately SEPARATE from `hint`. customer-pwa's local `Field` learned
   * this the hard way: one prop feeding both slots is what made every field on
   * `/me/profile` print its helper text twice. A counter sits on the label row;
   * a hint sits under the input; they are different things.
   */
  readonly counter?: string;
  /**
   * A validation failure, announced. Rendered with `role="alert"` so a screen
   * reader hears it when it appears, which `hint` deliberately does not do —
   * guidance that is always present must not interrupt.
   *
   * Takes precedence over `hint`: showing both is how a user ends up reading a
   * suggestion and an error that contradict each other.
   */
  readonly error?: string | null;
  /**
   * A virtual-keyboard hint. Separate from `type` on purpose: a phone field
   * wants `type='tel'` for autofill AND `inputMode='tel'` for the keypad, and
   * a numeric code wants a numeric pad without becoming a spinner input.
   */
  readonly inputMode?: 'text' | 'tel' | 'numeric' | 'email' | 'decimal' | 'search' | 'url';
  /**
   * Shown but not editable — a value the user supplied and the server has since
   * confirmed, like a verified phone number. Rendered with a brand-tinted fill
   * so "you cannot change this" is legible without a disabled control, which
   * would drop it out of the tab order and stop a screen reader announcing it.
   */
  readonly readOnly?: boolean;
  /**
   * Widen the letter spacing, for values read character by character rather
   * than as a word — a licence plate, a referral code, a reference number.
   * Tracking is what makes "WVX 4471" legible at a glance instead of a blur.
   */
  readonly tracking?: boolean;
  /** Hard cap enforced by the browser, in addition to any slicing in `onChange`. */
  readonly maxLength?: number;
}

const TICK = '✓';

export function PremiumField({
  label,
  value,
  onChange,
  type = 'text',
  required,
  placeholder,
  autoFocus,
  autoComplete,
  autoCapitalize,
  minLength,
  hint,
  hintTone = 'muted',
  icon,
  counter,
  error,
  inputMode,
  readOnly,
  tracking,
  maxLength,
}: PremiumFieldProps) {
  const [focused, setFocused] = useState(false);
  const IconComp = icon ? ICON_BY_NAME_LOCAL[icon] : null;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <FieldLabel>{label}</FieldLabel>
        {counter ? (
          <span style={{ fontSize: 12, color: 'var(--color-on-surface-muted, #64748b)' }}>
            {counter}
          </span>
        ) : null}
      </span>
      <div style={fieldShell(focused, readOnly)}>
        {IconComp ? (
          <span
            aria-hidden
            style={{
              display: 'grid',
              placeItems: 'center',
              paddingLeft: 14,
              color: focused
                ? 'var(--color-primary, #475569)'
                : 'var(--color-on-surface-muted, #94a3b8)',
              transition: 'color 140ms ease',
            }}
          >
            <IconComp />
          </span>
        ) : null}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required ?? false}
          placeholder={placeholder ?? ''}
          autoFocus={autoFocus ?? false}
          autoComplete={autoComplete ?? 'off'}
          autoCapitalize={autoCapitalize}
          inputMode={inputMode}
          readOnly={readOnly ?? false}
          maxLength={maxLength ?? undefined}
          minLength={minLength ?? undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            padding: '13px 14px',
            paddingLeft: icon ? 10 : 14,
            border: 'none',
            outline: 'none',
            // The shell draws the focus state. Without this the app-wide focus ring
            // lands on the bare input too: a second ring inside the rounded field.
            boxShadow: 'none',
            background: 'transparent',
            fontSize: 15,
            fontFamily: 'inherit',
            color: 'var(--color-on-surface, #0f172a)',
            cursor: readOnly ? 'default' : 'text',
            letterSpacing: tracking ? 1 : undefined,
          }}
        />
      </div>
      {error ? (
        <span role="alert" style={{ fontSize: 12, color: 'var(--color-accent, #dc2626)' }}>
          {error}
        </span>
      ) : hint ? (
        <FieldHint tone={hintTone}>{hint}</FieldHint>
      ) : null}
    </div>
  );
}

/**
 * The four strength rungs, in ascending order. Named rather than positional
 * so a caller cannot silently mis-order them — the meter indexes this by
 * level, and a swapped pair would tell someone their weak password is strong.
 */
export interface PasswordStrengthLabels {
  readonly tooShort: string;
  readonly gettingThere: string;
  readonly solid: string;
  readonly strong: string;
}

/** Visible strings in `PasswordField` beyond its label and placeholder. */
export interface PasswordFieldLabels {
  /** Visible text on the reveal toggle while the password is hidden. */
  readonly show: string;
  /** Visible text on the toggle while the password is showing. */
  readonly hide: string;
  /**
   * Accessible names for the toggle. Separate from the visible text because
   * "Show" alone is meaningless out of context to a screen-reader user.
   */
  readonly showPasswordAria: string;
  readonly hidePasswordAria: string;
  readonly strength: PasswordStrengthLabels;
}

// ── PhoneField (MY-prefixed) ───────────────────────────────────────────

export interface PhoneFieldProps {
  /**
   * Required, not defaulted. A default here shipped the English word "Phone"
   * to every ms/zh/ta signup form for as long as this component existed —
   * silently, because every caller simply omitted the prop and nothing in
   * lint can see inside a default parameter. Same reasoning as
   * `PwaInstallHintLabels` above: a shared component must not own copy.
   */
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly hint?: string;
  readonly hintTone?: FieldHintTone;
  readonly placeholder?: string;
  readonly required?: boolean;
}

export function PhoneField({
  label,
  value,
  onChange,
  hint,
  hintTone = 'muted',
  placeholder = '12-345 6789',
  required,
}: PhoneFieldProps) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <FieldLabel>{label}</FieldLabel>
      <div
        style={{
          ...fieldShell(focused),
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 12px',
            background: 'var(--tint-subtle)',
            borderRight: '1px solid var(--color-border)',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--color-on-surface-muted, #475569)',
            alignSelf: 'stretch',
          }}
        >
          <span style={{ fontSize: 16 }}>🇲🇾</span>
          <span>+60</span>
        </div>
        <input
          type="tel"
          inputMode="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="tel"
          placeholder={placeholder}
          required={required ?? false}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            padding: '13px 14px',
            border: 'none',
            outline: 'none',
            // The shell draws the focus state. Without this the app-wide focus ring
            // lands on the bare input too: a second ring inside the rounded field.
            boxShadow: 'none',
            background: 'transparent',
            fontSize: 15,
            fontFamily: 'inherit',
            color: 'var(--color-on-surface, #0f172a)',
          }}
        />
      </div>
      {hint ? <FieldHint tone={hintTone}>{hint}</FieldHint> : null}
    </div>
  );
}

// ── PasswordField (show/hide + strength meter) ─────────────────────────

export interface PasswordFieldProps {
  /** Required for the same reason as `PhoneFieldProps.label`. */
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly required?: boolean;
  /** Required: the old default, "At least 8 characters", was English copy. */
  readonly placeholder: string;
  /**
   * Every other visible string in the field: the show/hide toggle and the
   * strength meter. Grouped into one required object so adding a string here
   * is a compile error at all three signup callers rather than a silent
   * English regression.
   */
  readonly labels: PasswordFieldLabels;
  readonly autoComplete?: 'new-password' | 'current-password';
  readonly minLength?: number;
}

export function PasswordField({
  label,
  value,
  onChange,
  required,
  placeholder,
  labels,
  autoComplete = 'new-password',
  minLength = 8,
}: PasswordFieldProps) {
  const [focused, setFocused] = useState(false);
  const [show, setShow] = useState(false);
  const strength = passwordStrength(value);
  const showStrengthMeter = autoComplete === 'new-password' && value.length > 0;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <FieldLabel>{label}</FieldLabel>
      <div style={fieldShell(focused)}>
        <span
          aria-hidden
          style={{
            display: 'grid',
            placeItems: 'center',
            paddingLeft: 14,
            color: focused
              ? 'var(--color-primary, #475569)'
              : 'var(--color-on-surface-muted, #94a3b8)',
          }}
        >
          <LockIcon />
        </span>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required={required ?? false}
          minLength={minLength}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            padding: '13px 14px 13px 10px',
            border: 'none',
            outline: 'none',
            // The shell draws the focus state. Without this the app-wide focus ring
            // lands on the bare input too: a second ring inside the rounded field.
            boxShadow: 'none',
            background: 'transparent',
            fontSize: 15,
            fontFamily: 'inherit',
            color: 'var(--color-on-surface, #0f172a)',
          }}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? labels.hidePasswordAria : labels.showPasswordAria}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-on-surface-muted, #64748b)',
            padding: '0 14px',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          {show ? labels.hide : labels.show}
        </button>
      </div>
      {showStrengthMeter ? <StrengthMeter level={strength} labels={labels.strength} /> : null}
    </div>
  );
}

// ── StrengthMeter ──────────────────────────────────────────────────────

function StrengthMeter({
  level,
  labels,
}: {
  readonly level: 0 | 1 | 2 | 3;
  readonly labels: PasswordStrengthLabels;
}) {
  const segments = [
    { active: level >= 1, color: level === 1 ? '#ef4444' : level === 2 ? '#f59e0b' : '#475569' },
    { active: level >= 2, color: level === 2 ? '#f59e0b' : '#475569' },
    { active: level >= 3, color: '#475569' },
  ];
  // Indexed by `level`, so the tuple order is load-bearing — see the type.
  const copy = [labels.tooShort, labels.gettingThere, labels.solid, labels.strong];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
      <div style={{ display: 'flex', gap: 4, flex: 1 }}>
        {segments.map((s, i) => (
          <span
            key={i}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 999,
              background: s.active ? s.color : 'var(--color-border)',
              transition: 'all 200ms ease',
            }}
          />
        ))}
      </div>
      <span
        style={{
          fontSize: 11,
          color: 'var(--color-on-surface-muted, #64748b)',
          minWidth: 78,
          textAlign: 'right',
        }}
      >
        {copy[level]}
      </span>
    </div>
  );
}

function passwordStrength(pw: string): 0 | 1 | 2 | 3 {
  if (pw.length < 8) return 0;
  let score = 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) || /[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(3, score) as 0 | 1 | 2 | 3;
}

// ── Spinner (used by submit buttons) ───────────────────────────────────

export function Spinner({
  size = 16,
  color = '#ffffff',
}: {
  readonly size?: number;
  readonly color?: string;
}) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        border: `2px solid ${color}38`,
        borderTopColor: color,
        animation: 'kilatSpin 700ms linear infinite',
        display: 'inline-block',
      }}
    >
      <style>{`@keyframes kilatSpin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

// ── Label + hint primitives ────────────────────────────────────────────

function FieldLabel({ children }: { readonly children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--color-on-surface, #334155)',
        letterSpacing: 0.2,
      }}
    >
      {children}
    </span>
  );
}

function FieldHint({
  tone,
  children,
}: {
  readonly tone: FieldHintTone;
  readonly children: React.ReactNode;
}) {
  const colour =
    tone === 'success'
      ? 'var(--color-primary, #475569)'
      : tone === 'warn'
        ? '#b45309'
        : 'var(--color-on-surface-muted, #64748b)';
  return (
    <span
      aria-live="polite"
      style={{
        fontSize: 12,
        color: colour,
        lineHeight: 1.5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {tone === 'success' ? (
        <span aria-hidden style={{ fontSize: 10 }}>
          {TICK}
        </span>
      ) : null}
      {children}
    </span>
  );
}

// ── Shared shell ───────────────────────────────────────────────────────

function fieldShell(focused: boolean, readOnly?: boolean): React.CSSProperties {
  // A confirmed value reads as settled rather than disabled: brand-tinted fill
  // and border, full-strength text, still focusable. A greyed-out control would
  // say "broken" where this needs to say "already done".
  if (readOnly) {
    return {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      background: 'rgba(var(--color-primary-rgb), 0.06)',
      border: '1px solid rgba(var(--color-primary-rgb), 0.22)',
      borderRadius: 12,
      transition: 'all 140ms ease',
    };
  }
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    background: focused ? 'var(--color-surface, #ffffff)' : 'var(--color-surface-sunken, #f8fafc)',
    border: `1px solid ${focused ? 'var(--color-primary, #475569)' : 'var(--color-border-strong)'}`,
    borderRadius: 12,
    transition: 'all 140ms ease',
    boxShadow: focused
      ? '0 0 0 4px rgba(var(--color-primary-rgb), 0.12), 0 1px 2px var(--tint-subtle)'
      : '0 1px 2px var(--tint-subtle)',
  };
}

// ── Local icon-by-name map (only the ones PremiumField cares about) ───

const ICON_BY_NAME_LOCAL = {
  building: BuildingIcon,
  user: UserIcon,
  mail: MailIcon,
  tag: TagIcon,
  // The other icons aren't field-leading by default; importers reach for
  // them via `@kilat/ui` directly if they need them inline.
} as const;
